import { Reorder } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { TbArrowRight } from "react-icons/tb";
import styled, { css } from "styled-components";
import Button from "../../../components/Button";
import Checkbox from "../../../components/Checkbox";
import { Title3 } from "../../../styles/typography";
import ReorderableChapterRow, {
  CHECKBOX_STYLE,
  DESKTOP_GRID,
  DESKTOP_REORDER_GRID,
  MOBILE_BREAKPOINT,
  MOBILE_SELECT_GRID,
} from "./ReorderableChapterRow";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) =>
    props.$reorderMode ? DESKTOP_REORDER_GRID : DESKTOP_GRID};
  align-items: center;
  gap: 12px;
  padding: 0 1rem;
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgb(${(props) => props.theme.colors.white});
  border-bottom: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    ${(props) =>
      props.$reorderMode
        ? css`
            display: none;
          `
        : css`
            grid-template-columns: ${MOBILE_SELECT_GRID};
            & > *:nth-child(2),
            & > *:nth-child(4),
            & > *:nth-child(5),
            & > *:nth-child(6) {
              display: none;
            }
          `}
  }
`;

const HeaderCell = styled(Title3)`
  padding: 1rem 0;
  color: rgb(${(props) => props.theme.colors.neutral400});
  font-weight: 600;
  font-size: 1rem;
  text-transform: uppercase;
`;

const DesktopOnlyLabel = styled.span`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const MobileOnlyLabel = styled.span`
  display: none;
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: inline;
  }
`;

const EmptyState = styled.div`
  display: flex;
  justify-content: center;
  padding: 2rem 1rem;
  background: rgb(${(props) => props.theme.colors.white});
`;

/**
 * @param {{
 *   memories: object[],
 *   reorderMode: boolean,
 *   selectedMemories: string[],
 *   allChecked: boolean,
 *   onToggleSelect: (memoryID: string) => void,
 *   onSelectAll: () => void,
 *   onReorder: (memories: object[]) => void,
 *   onDragEnd: () => void,
 *   onDeleteRequest: (memoryID: string) => void,
 * }} props
 */
const ReorderableChapterList = ({
  memories,
  reorderMode,
  selectedMemories,
  allChecked,
  onToggleSelect,
  onSelectAll,
  onReorder,
  onDragEnd,
  onDeleteRequest,
}) => {
  const [quickMenuId, setQuickMenuId] = useState(null);
  const [movedId, setMovedId] = useState(null);
  const movedTimerRef = useRef(null);
  const groupRef = useRef(null);
  // Only one scroll-driven swap per render: the swap invalidates the row
  // geometry used to detect it, so further checks must wait for React to
  // commit the new order.
  const reorderPendingRef = useRef(false);

  useEffect(() => () => clearTimeout(movedTimerRef.current), []);

  useEffect(() => {
    reorderPendingRef.current = false;
  });

  const handleQuickMenuToggle = (memoryID) =>
    setQuickMenuId((prev) => (prev === memoryID ? null : memoryID));

  const handleScrollReorder = (memoryID, offset, direction) => {
    if (!direction || reorderPendingRef.current) return;
    const group = groupRef.current;
    if (!group) return;
    const index = memories.findIndex((memory) => memory.memoryID === memoryID);
    const nextIndex = index + direction;
    if (index === -1 || nextIndex < 0 || nextIndex >= memories.length) return;
    const itemEl = group.children[index];
    const nextEl = group.children[nextIndex];
    if (!itemEl || !nextEl) return;
    const nextCenter = nextEl.offsetTop + nextEl.offsetHeight / 2;
    const shouldSwap =
      direction === 1
        ? itemEl.offsetTop + itemEl.offsetHeight + offset > nextCenter
        : itemEl.offsetTop + offset < nextCenter;
    if (!shouldSwap) return;
    const next = [...memories];
    next.splice(index, 1);
    next.splice(nextIndex, 0, memories[index]);
    reorderPendingRef.current = true;
    onReorder(next);
  };

  const handleQuickMove = (memoryID, where) => {
    const item = memories.find((memory) => memory.memoryID === memoryID);
    if (!item) return;
    const rest = memories.filter((memory) => memory.memoryID !== memoryID);
    const next = where === "top" ? [item, ...rest] : [...rest, item];
    setQuickMenuId(null);
    setMovedId(memoryID);
    clearTimeout(movedTimerRef.current);
    movedTimerRef.current = setTimeout(() => setMovedId(null), 900);
    onReorder(next);
    onDragEnd();
  };

  if (memories.length === 0) {
    return (
      <EmptyState>
        <Button variant="primary" to="/memories/new">
          Create your first chapter <TbArrowRight />
        </Button>
      </EmptyState>
    );
  }

  return (
    <div>
      <HeaderRow $reorderMode={reorderMode}>
        {!reorderMode && (
          <HeaderCell as="div">
            <Checkbox
              checked={allChecked}
              selectItem={onSelectAll}
              style={CHECKBOX_STYLE}
            />
          </HeaderCell>
        )}
        <HeaderCell>#</HeaderCell>
        <HeaderCell>
          <DesktopOnlyLabel>Chapter title</DesktopOnlyLabel>
          <MobileOnlyLabel>Chapter details</MobileOnlyLabel>
        </HeaderCell>
        <HeaderCell>Created at</HeaderCell>
        <HeaderCell>{reorderMode ? "Reorder" : "Updated at"}</HeaderCell>
        {!reorderMode && <HeaderCell as="div" />}
      </HeaderRow>
      <Reorder.Group
        as="div"
        axis="y"
        ref={groupRef}
        values={memories}
        onReorder={onReorder}
        style={{ position: "relative" }}
      >
        {memories.map((memory, idx) => (
          <ReorderableChapterRow
            key={memory.memoryID}
            memory={memory}
            position={idx + 1}
            reorderMode={reorderMode}
            checked={selectedMemories.includes(memory.memoryID)}
            onToggleSelect={onToggleSelect}
            onDragEnd={onDragEnd}
            quickMenuOpen={
              memory.memoryID === quickMenuId &&
              selectedMemories.includes(memory.memoryID)
            }
            moved={memory.memoryID === movedId}
            onQuickMenuToggle={handleQuickMenuToggle}
            onQuickMove={handleQuickMove}
            onScrollReorder={handleScrollReorder}
            isFirst={idx === 0}
            isLast={idx === memories.length - 1}
            onDeleteRequest={onDeleteRequest}
          />
        ))}
      </Reorder.Group>
    </div>
  );
};

export default ReorderableChapterList;
